import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Edit, Trash2, MapPin, Phone, Mail, Calendar, Users,
  Building2, Clock, Wifi, ParkingCircle, Baby, Building,
  TrendingUp, Facebook, Instagram, Twitter, Youtube, Globe,
  Accessibility
} from 'lucide-react';
import { branchAPI } from '../../../services/api';
import { showToast } from '../../../utils/toasts';
import DeleteBranchModal from '../../../components/branch/DeleteBranchModal';
import FeatureGate from '../../../components/access/FeatureGate';
import { departmentAPI } from '../../../services/api';
import PermissionGuard from '../../../components/guards/PermissionGuard';

const BranchDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [branch, setBranch] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departments, setDepartments] = useState<any[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Convert 24-hour time to 12-hour format
  const formatTime = (time: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  useEffect(() => {
    fetchBranch();
    fetchStats();
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchDepartments();
    }
  }, [id]);

  const fetchBranch = async () => {
    try {
      setLoading(true);
      const response = await branchAPI.getBranch(id);
      setBranch(response.data.data.branch);
    } catch (error: any) {
      showToast.error('Failed to load branch');
      navigate('/branches');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await branchAPI.getStats(id);
      setStats(response.data.data.statistics);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await departmentAPI.getDepartments({ branchId: id });
      setDepartments(response.data.data.departments || []);
    } catch (error) {
      console.error('Failed to load departments');
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (permanent: boolean) => {
    try {
      await branchAPI.deleteBranch(id, permanent);
      showToast.success(permanent ? 'Branch deleted permanently' : 'Branch archived');
      navigate('/branches');
    } catch (error: any) {
      showToast.error(error?.response?.data?.message || 'Failed to delete branch');
    }
  };

  const formatDate = (date: string) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: any = {
      main: 'bg-purple-100 dark:bg-purple-900/20 text-primary-700 dark:text-primary-300',
      branch: 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-blue-300',
      campus: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
      satellite: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
    };
    return colors[type] || colors.branch;
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: any = {
      active: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300',
      inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      closed: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300',
      upcoming: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    };
    return colors[status] || colors.inactive;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading branch details...</p>
        </div>
      </div>
    );
  }

  if (!branch) {
    return null;
  }

  return (
    <FeatureGate feature={'branchManagement'}>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-32"></div>

      <div className="max-w-8xl mx-auto px-6 -mt-16 pb-12">
        {/* Back button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/branches')}
            className="flex items-center text-white hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Branches
          </button>
        </div>

        {/* Branch Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-6">
              <div className="w-24 h-24 bg-primary-100 dark:bg-primary-900/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-12 h-12 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {branch.name}
                  </h1>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTypeBadgeColor(branch.type)}`}>
                    {branch.type}
                  </span>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadgeColor(branch.status)}`}>
                    {branch.status}
                  </span>
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-3">
                  Code: <span className="font-semibold">{branch.code}</span>
                </p>
                {branch.description && (
                  <p className="text-gray-600 dark:text-gray-400 max-w-3xl">
                    {branch.description}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(`/branches/${id}/members`)}
                className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <Users className="w-4 h-4 mr-2" />
                View Members
              </button>
              <PermissionGuard permission="branches.edit">
                <button
                  onClick={() => navigate(`/branches/${id}/edit`)}
                  className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </button>
              </PermissionGuard>
              <PermissionGuard permission="branches.delete">
              <button
                onClick={handleDelete}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
              </PermissionGuard>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                Contact Information
              </h2>
              <div className="space-y-3 mt-4 p-6 pt-0">
                {branch.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{branch.phone}</span>
                  </div>
                )}
                {branch.alternatePhone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{branch.alternatePhone}</span>
                  </div>
                )}
                {branch.email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{branch.email}</span>
                  </div>
                )}
                {branch.address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="text-gray-700 dark:text-gray-300">
                      <p>{branch.address.street}</p>
                      <p>
                        {branch.address.city}
                        {branch.address.region && `, ${branch.address.region}`}
                        {branch.address.state && `, ${branch.address.state}`}
                      </p>
                      <p>{branch.address.country}</p>
                      {branch.address.landmark && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Landmark: {branch.address.landmark}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Leadership */}
            {(branch.pastor || branch.assistant || (branch.leaders && branch.leaders.length > 0)) && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex items-center space-x-2 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                  <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Leadership
                  </h2>
                </div>
                <div className="space-y-4 mt-4 p-6 pt-0">
                  {branch.pastor && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Pastor</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {branch.pastor.firstName} {branch.pastor.lastName}
                      </p>
                      {branch.pastor.email && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{branch.pastor.email}</p>
                      )}
                    </div>
                  )}
                  {branch.assistant && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Assistant Pastor</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {branch.assistant.firstName} {branch.assistant.lastName}
                      </p>
                      {branch.assistant.email && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{branch.assistant.email}</p>
                      )}
                    </div>
                  )}
                  {branch.leaders && branch.leaders.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Branch Leaders</p>
                      <div className="space-y-2">
                        {branch.leaders.map((leader: any) => (
                          <div key={leader._id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {leader.firstName} {leader.lastName}
                            </span>
                            {leader.phone && (
                              <span className="text-sm text-gray-600 dark:text-gray-400">{leader.phone}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Times */}
            {branch.serviceTimes && branch.serviceTimes.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex items-center space-x-2 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                  <Clock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Service Times
                  </h2>
                </div>
                <div className="space-y-3 mt-4 p-6 pt-0">
                  {branch.serviceTimes.map((service: any, index: number) => (
                    <div key={index} className="flex items-start justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:shadow-sm transition-shadow">
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-1">{service.service}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{service.day}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-primary-600 dark:text-primary-400 text-lg">{formatTime(service.startTime)}</p>
                        {service.endTime && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">to {formatTime(service.endTime)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Facilities & Amenities */}
            {branch.facilities && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="flex items-center space-x-2 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                  <Wifi className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Facilities & Amenities
                  </h2>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 mt-4 p-6 pt-0">
                  {branch.facilities.capacity && (
                    <div className="flex items-center space-x-3 py-3 px-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Building className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Capacity</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{branch.facilities.capacity}</p>
                      </div>
                    </div>
                  )}
                  {branch.facilities.parking?.available && (
                    <div className="flex items-center space-x-3 py-3 px-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <ParkingCircle className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Parking</p>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {branch.facilities.parking.spaces || 'Available'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 p-6 pt-0 mb-1">
                  {branch.facilities.childcare && (
                    <span className="flex items-center px-3 py-1.5 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-full text-sm">
                      <Baby className="w-4 h-4 mr-1.5" />
                      Childcare
                    </span>
                  )}
                  {branch.facilities.accessibility?.wheelchair && (
                    <span className="flex items-center px-3 py-1.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm">
                      <Accessibility className="w-4 h-4 mr-1.5" />
                      Wheelchair Access
                    </span>
                  )}
                  {branch.facilities.accessibility?.elevator && (
                    <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/20 text-primary-700 dark:text-primary-400 rounded-full text-sm">
                      Elevator
                    </span>
                  )}
                  {branch.facilities.accessibility?.assistiveListening && (
                    <span className="px-3 py-1.5 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 rounded-full text-sm">
                      Assistive Listening
                    </span>
                  )}
                  {branch.facilities.amenities?.map((amenity: string, index: number) => (
                    <span key={index} className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media */}
            {branch.socialMedia && Object.values(branch.socialMedia).some(v => v) && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-3 bg-gray-50 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                  Social Media & Website
                </h2>
                <div className="flex flex-wrap gap-3 mt-4 p-6 pt-0">
                  {branch.socialMedia.facebook && (
                    <a
                      href={branch.socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <Facebook className="w-4 h-4 mr-2" />
                      Facebook
                    </a>
                  )}
                  {branch.socialMedia.instagram && (
                    <a
                      href={branch.socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-pink-100 dark:bg-pink-900/20 text-pink-700 dark:text-pink-400 rounded-lg hover:bg-pink-200 dark:hover:bg-pink-900/30 transition-colors"
                    >
                      <Instagram className="w-4 h-4 mr-2" />
                      Instagram
                    </a>
                  )}
                  {branch.socialMedia.twitter && (
                    <a
                      href={branch.socialMedia.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-sky-100 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 rounded-lg hover:bg-sky-200 dark:hover:bg-sky-900/30 transition-colors"
                    >
                      <Twitter className="w-4 h-4 mr-2" />
                      Twitter
                    </a>
                  )}
                  {branch.socialMedia.youtube && (
                    <a
                      href={branch.socialMedia.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Youtube className="w-4 h-4 mr-2" />
                      YouTube
                    </a>
                  )}
                  {branch.socialMedia.website && (
                    <a
                      href={branch.socialMedia.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Stats & Info */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex items-center space-x-2 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                <TrendingUp className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Statistics
                </h2>
              </div>
              <div className="space-y-4 p-4">
<div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Members</p>
                    <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                      {branch.statistics?.memberCount || 0}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/branches/${id}/members`)}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    View All
                  </button>
                </div>
                {/* Average attendance stat removed. Use event-based attendance instead. */}
              </div>
            </div>

            {/* Branch Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b-1 border-primary-200 dark:border-primary-900 px-6 pt-2">
                Branch Information
              </h2>
              <div className="space-y-3 mt-4 p-6 pt-0">
                {branch.establishedDate && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Established</p>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <p className="text-gray-900 dark:text-gray-100">{formatDate(branch.establishedDate)}</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Created</p>
                  <p className="text-gray-900 dark:text-gray-100">{formatDate(branch.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Last Updated</p>
                  <p className="text-gray-900 dark:text-gray-100">{formatDate(branch.updatedAt)}</p>
                </div>
              </div>
            </div>

            {/* Departments */}
            {departments.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-3 bg-primary-100 dark:bg-gray-700 border-b border-primary-200 dark:border-primary-900 px-6 pt-2">
                  Departments ({departments.length})
                </h2>
                <div className={`space-y-2 p-6 pt-0 ${departments.length > 5 ? 'max-h-96 overflow-y-auto' : ''}`}>
                  {departments.map((dept: any) => (
                    <div
                      key={dept._id}
                      onClick={() => navigate(`/departments/${dept._id}`)}
                      className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:shadow-md hover:bg-primary-100 dark:hover:bg-blue-900/30 transition-all cursor-pointer flex-shrink-0"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{dept.name}</p>
                        {dept.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{dept.description}</p>
                        )}
                      </div>
                      <div className="ml-4 flex flex-col items-end">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                          {dept.memberCount || 0} {dept.memberCount === 1 ? 'member' : 'members'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteBranchModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteConfirm}
        branchName={branch?.name || ''}
        memberCount={branch?.statistics?.memberCount || 0}
      />
    </div>
    </FeatureGate>
  );
};

export default BranchDetails;